import React, { useCallback, useContext, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import flowRight from 'lodash-es/flowRight';
import orderBy from 'lodash-es/orderBy';
import { v4 as uuid } from 'uuid';
import { Formik } from 'formik';
import { withStyles } from '@material-ui/core/styles';
import { Table } from 'core';
import { AuthContext } from 'core/components/auth';
import { withStore } from 'core/store';
import { Card } from 'core/components/card';
import { Loading } from 'core/components/loading';
import Section from 'core/components/section';
import styles from './incidents.styles';
import Footer from '../footer';
import IncidentTableRows, { IncidentTableHeader } from './incidents-table';
import ConfirmRemoveIncidentsDialog from './confirm-remove-incidents-dialog';

const formatViolation = (violation, options) => ({
  type: violation.class,
  id: uuid(),
  source: violation.source,
  isCourtRecord: violation.isCourtRecord || false,
  ...options
});

const getHomeViolations = (violations) => {
  if (!violations) {
    return [];
  }

  const homeViolations = JSON.parse(violations);

  // Selects a single incident per date
  return Object.entries(homeViolations).reduce(
    (acc, [date, [homeViolation]]) =>
      homeViolation
        ? [
            ...acc,
            formatViolation(homeViolation, {
              date
            })
          ]
        : acc,
    []
  );
};

const getHomeSingleViolationDates = (violations) => {
  if (!violations) {
    return [];
  }

  const homeViolations = JSON.parse(violations);
  // TODO: clarify type to be used for major and minor violations

  return [
    ...Object.entries(homeViolations.MIN).map(([date, violation]) => formatViolation(violation, { date, type: 'MIN' })),
    ...Object.entries(homeViolations.MAJ).map(([date, violation]) => formatViolation(violation, { date, type: 'MAJ' }))
  ];
};

const getHomeClaims = (claims) => {
  if (!claims) {
    return [];
  }

  try {
    const homeClaims = JSON.parse(claims);

    return Object.entries(homeClaims).reduce(
      (acc, [date, claimList]) =>
        claimList?.length
          ? [...acc, ...claimList.map((claim) => formatViolation(claim, { date, category: 'claim' }))]
          : acc,
      []
    );
  } catch {
    console.error('[incidents] Failed to parse homeClaimDates');
    return [];
  }
};

const getHomeIncidents = (policy) =>
  policy?.segments?.reduce(
    (acc, segment) => [
      ...acc,
      ...getHomeViolations(segment.homeViolationDates),
      ...getHomeSingleViolationDates(segment.homeSingleViolationDates),
      ...getHomeClaims(segment.homeClaimDates)
    ],
    []
  );

const getAutoViolation = (autoViolationDates, driver) => {
  if (!autoViolationDates) {
    return [];
  }

  const autoViolations = JSON.parse(autoViolationDates);
  return Object.entries(autoViolations).reduce((acc, [date, violations]) => {
    // choose violation with highest points. (this will choose the first violation if there's only one)
    const violation = violations?.length
      ? violations.reduce((prev, curr) => (prev.points > curr.points ? prev : curr))
      : violations[0];
    return [
      ...acc,
      formatViolation(violation, {
        date,
        driver: `${driver.firstName} ${driver.lastName}`
      })
    ];
  }, []);
};

const getAutoPolicySegmentIncidents = (segment) =>
  segment?.drivers?.reduce((acc, driver) => [...acc, ...getAutoViolation(driver.autoViolationDates, driver)], []) || [];

const getAutoPolicyIncidents = (policy) =>
  policy?.segments?.reduce((acc, segment) => [...acc, ...getAutoPolicySegmentIncidents(segment)], []) || [];

const Incidents = ({
  loadingPreview,
  handleChangePolicy,
  classes,
  store: {
    account: {
      policies: {
        policy: { loading, policy, violationsForRenewal }
      }
    }
  }
}) => {
  const session = useContext(AuthContext);
  const [sortDirection, setSortDirection] = useState('asc');
  const [selectedIncidents, setSelectedIncidents] = useState({});
  const [incidentsToRemove, setIncidentsToRemove] = useState([]);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);

  const showPreviewIncident = useMemo(
    () => session?.isService && !session.viewOnly && !!Object.values(selectedIncidents).length,
    [selectedIncidents, session]
  );

  const canScrub = session?.canScrubIncidents;

  const isAutoPolicyType = policy.policyType === 'A';

  const rawIncidents = useMemo(() => {
    if (!policy) {
      return [];
    }

    return policy.policyType === 'A' ? getAutoPolicyIncidents(policy) : getHomeIncidents(policy);
  }, [policy]);

  const incidents = useMemo(() => orderBy(rawIncidents, ['date'], [sortDirection]), [rawIncidents, sortDirection]);

  const sort = (_columnId, _active, direction) => setSortDirection(direction);

  const selectIncident = (incident, checked) => {
    setSelectedIncidents((prev) => {
      if (checked) {
        return { ...prev, [incident.id]: incident };
      }

      const { [incident.id]: currentIncident, ...others } = prev;

      return others;
    });
  };

  const handleConfirmRemove = useCallback(() => {
    const claims = incidentsToRemove.filter((item) => item.category === 'claim');
    const violations = incidentsToRemove.filter((item) => item.category !== 'claim');
    const violationDates = violations.map((item) => item.date);
    const claimsToScrub = claims.map((item) => ({ date: item.date, claimClass: item.type }));
    // Fire the mutation first so the preview modal opens before we close the confirm dialog,
    // preventing a white flash between the two modals
    handleChangePolicy({ immediateBillOrRefundAmount: false, id: policy.id }, violationDates, claimsToScrub);
    setConfirmDialogOpen(false);
    setIncidentsToRemove([]);
    setSelectedIncidents({});
  }, [incidentsToRemove, policy, handleChangePolicy]);

  const handleCloseConfirmDialog = useCallback(() => {
    setConfirmDialogOpen(false);
    setIncidentsToRemove([]);
  }, []);

  const handleChange = useCallback(() => {
    const selected = Object.values(selectedIncidents);
    setIncidentsToRemove(selected);
    setConfirmDialogOpen(true);
  }, [selectedIncidents]);

  const [futureIncidentsSortDirection, setFutureIncidentsSortDirection] = useState('asc');

  const orderedFutureIncidents = useMemo(
    () => orderBy(violationsForRenewal, ['date'], [futureIncidentsSortDirection]),
    [violationsForRenewal, futureIncidentsSortDirection]
  );

  return (
    <Formik
      initialValues={{
        checkbox: []
      }}
      onSubmit={handleChange}
    >
      {({ submitForm }) => (
        <>
          <div>
            <Section title="Current Incidents & Violations">
              {loading ? (
                <Loading type="secondary" />
              ) : (
                <Card className={classes.tableContainer} type="secondary">
                  {incidents.length > 0 ? (
                    <Table
                      mode={'dark'}
                      dataCy="incidents"
                      selfContained={false}
                      header={
                        <IncidentTableHeader
                          canScrub={canScrub}
                          isAutoPolicy={isAutoPolicyType}
                          onSort={sort}
                          classes={classes}
                        />
                      }
                      body={
                        <IncidentTableRows
                          canScrub={canScrub}
                          selectedIncidents={selectedIncidents}
                          incidents={incidents}
                          classes={classes}
                          isAutoPolicy={isAutoPolicyType}
                          onSelect={selectIncident}
                        />
                      }
                    />
                  ) : (
                    <p> No Incidents</p>
                  )}
                </Card>
              )}
            </Section>
            {showPreviewIncident && (
              <Footer loading={loadingPreview} title="Remove Selected Incidents" onClick={submitForm} />
            )}
          </div>
          {violationsForRenewal.length > 0 && (
            <Section title="Future Term Incidents & Violations">
              {loading ? (
                <Loading type="secondary" />
              ) : (
                <Card className={classes.tableContainer} type="secondary">
                  <Table
                    mode={'dark'}
                    dataCy="incidents"
                    selfContained={false}
                    header={
                      <IncidentTableHeader
                        canScrub={false}
                        isAutoPolicy={isAutoPolicyType}
                        onSort={(_columnId, _active, direction) => setFutureIncidentsSortDirection(direction)}
                        classes={classes}
                      />
                    }
                    body={
                      <IncidentTableRows
                        canScrub={false}
                        incidents={orderedFutureIncidents}
                        classes={classes}
                        isAutoPolicy={isAutoPolicyType}
                      />
                    }
                  />
                </Card>
              )}
            </Section>
          )}
          {confirmDialogOpen && incidentsToRemove.length > 0 && (
            <ConfirmRemoveIncidentsDialog
              open={confirmDialogOpen}
              incidents={incidentsToRemove}
              isAutoPolicy={isAutoPolicyType}
              onConfirm={handleConfirmRemove}
              onClose={handleCloseConfirmDialog}
            />
          )}
        </>
      )}
    </Formik>
  );
};

Incidents.propTypes = {
  classes: PropTypes.object.isRequired,
  store: PropTypes.object.isRequired,
  loadingPreview: PropTypes.bool.isRequired,
  handleChangePolicy: PropTypes.func.isRequired
};

export default flowRight(withStyles(styles), withStore)(Incidents);
